/* global localStorage */
import * as MutationTypes from 'store/mutation-types'
import errorMixin from '../mixins/logHandler.js'

// init state
const state = {
  allComparedEvasiveManeuversIds: {},
  customEvasiveScenarios: [],
  customEvasiveReports: [],
  customEvasiveLoading: false,
  customEvasiveErrors: []
}

const getters = {
  allComparedEvasiveManeuversIds() {
    return state.allComparedEvasiveManeuversIds
  },
  customEvasiveScenarios() {
    return state.customEvasiveScenarios
  },
  customEvasiveReports() {
    return state.customEvasiveReports
  },
  customEvasiveLoading() {
    return state.customEvasiveLoading
  },
  customEvasiveErrors() {
    return state.customEvasiveErrors
  }
}

const mutations = {
  UPDATE_COMPARED_EVASIVE_MANEUVERS(state, { data, key }) {
    state.allComparedEvasiveManeuversIds = {
      ...state.allComparedEvasiveManeuversIds,
      [key]: data
    }
  },
  SET_COMPARED_EVASIVE_MANEUVERS(state, { data }) {
    const keys = Object.keys(data)
    state.allComparedEvasiveManeuversIds = data
    keys.forEach((key) => {
      state.allComparedEvasiveManeuversIds[key] = new Set(data[key])
    })
  },
  UPDATE_CUSTOM_EVASIVE_SCENARIOS(state, data) {
    state.customEvasiveScenarios = data
  },
  UPDATE_CUSTOM_EVASIVE_REPORTS(state, data) {
    state.customEvasiveReports = data
  },
  UPDATE_CUSTOM_EVASIVE_LOADING(state, data) {
    state.customEvasiveLoading = data
  },
  UPDATE_CUSTOM_EVASIVE_ERRORS(state, data) {
    state.customEvasiveErrors = data
  }
}

const updateLocallySavedComparedEvasiveManeuversIds = (state) => {
  const currentMissionId = localStorage.getItem('currentMissionId')
  let parameters = localStorage.getItem(
    'EvasiveManeuverDashboardLocalParameters'
  )
  if (!parameters) {
    parameters = JSON.stringify({})
  }
  if (parameters) {
    const parsedParameters = JSON.parse(parameters)
    // convert the set from vuex to an array to be able to save it in local storage since sets are not serializable
    if (!parsedParameters[currentMissionId]) {
      parsedParameters[currentMissionId] = {}
    }
    if (!parsedParameters[currentMissionId]['allComparedEvasiveManeuversIds']) {
      parsedParameters[currentMissionId]['allComparedEvasiveManeuversIds'] = {}
    }
    const keys = Object.keys(state.allComparedEvasiveManeuversIds)
    keys.forEach((key) => {
      parsedParameters[currentMissionId]['allComparedEvasiveManeuversIds'][
        key
      ] = Array.from(state.allComparedEvasiveManeuversIds[key])
    })
    localStorage.setItem(
      'EvasiveManeuverDashboardLocalParameters',
      JSON.stringify(parsedParameters)
    )
  } else {
    // init the local storage
    const newParameters = {
      [currentMissionId]: {
        allComparedEvasiveManeuversIds: structuredClone(
          state.allComparedEvasiveManeuversIds
        )
      }
    }
    localStorage.setItem(
      'EvasiveManeuverDashboardLocalParameters',
      JSON.stringify(newParameters)
    )
  }
}

const actions = {
  async restoreLocallySavedComparedEvasiveManeuversIds({ commit }) {
    const json = localStorage.getItem('EvasiveManeuverDashboardLocalParameters')
    if (json) {
      const parameters = JSON.parse(json)
      const currentMissionId = localStorage.getItem('currentMissionId')
      if (parameters && parameters[currentMissionId]) {
        // convert the array from local storage to a set to be able to use it in vuex
        const keys = Object.keys(
          parameters[currentMissionId]['allComparedEvasiveManeuversIds']
        )
        const allComparedEvasiveManeuversIds =
          parameters[currentMissionId]['allComparedEvasiveManeuversIds']
        keys.forEach((key) => {
          allComparedEvasiveManeuversIds[key] = new Set(
            allComparedEvasiveManeuversIds[key]
          )
        })
        await commit(MutationTypes.SET_COMPARED_EVASIVE_MANEUVERS, {
          data: allComparedEvasiveManeuversIds || {}
        })
      }
    }
  },
  async addToComparedEvasiveManeuversIds({ commit }, { id, key }) {
    const currentIdsSet = state.allComparedEvasiveManeuversIds[key] || new Set()
    const newValue = new Set([...currentIdsSet]).add(id)

    await commit(MutationTypes.UPDATE_COMPARED_EVASIVE_MANEUVERS, {
      data: newValue,
      key
    })
    updateLocallySavedComparedEvasiveManeuversIds(state)
  },
  async deleteFromComparedEvasiveManeuversIds({ commit }, { id, key }) {
    const currentIdsSet = state.allComparedEvasiveManeuversIds[key] || new Set()
    const newValue = new Set([...currentIdsSet])
    newValue.delete(id)
    await commit(MutationTypes.UPDATE_COMPARED_EVASIVE_MANEUVERS, {
      data: newValue,
      key
    })
    updateLocallySavedComparedEvasiveManeuversIds(state)
  },
  async refreshCustomEvasiveScenarios({ commit }) {
    const user = this.getters.currentUser
    await commit(MutationTypes.UPDATE_CUSTOM_EVASIVE_LOADING, true)
    await commit(MutationTypes.UPDATE_CUSTOM_EVASIVE_SCENARIOS, [])
    await commit(MutationTypes.UPDATE_CUSTOM_EVASIVE_REPORTS, [])
    await commit(MutationTypes.UPDATE_CUSTOM_EVASIVE_ERRORS, [])
    const customScenarios = await this._vm.$http
      .get('/users/' + user.id + '/customevasivescenariodefinitions')
      .then((response) => {
        return response.data.filter((scenario) => scenario.missionId != null)
      })
      .catch((e) => {
        errorMixin.methods.displayMessage(
          'Error while retrieving custom scenarios. ',
          e,
          'NumericalMissionErrorDisplay',
          'error'
        )
        return []
      })
    await commit(MutationTypes.UPDATE_CUSTOM_EVASIVE_SCENARIOS, customScenarios)
    const promises = customScenarios.map((scenario) => {
      let params = {
        scenarioId: scenario.id,
        missionId: scenario.missionId
      }
      return this._vm.$http
        .get(
          '/users/' +
            user.id +
            '/customevasivescenariodefinitions/buildcustomevasivereport',
          { params }
        )
        .then((response) => {
          return { ...response.data, id: scenario.name }
        })
        .catch(async (e) => {
          const newErrors = [
            ...state.customEvasiveErrors.filter(
              (error) => error.name !== scenario.name
            ),
            {
              name: scenario.name,
              message: errorMixin.methods.getErrorMessage(e)
            }
          ]
          await commit(MutationTypes.UPDATE_CUSTOM_EVASIVE_ERRORS, newErrors)
          return null
        })
    })
    const customReports = (
      await Promise.all(promises).catch((e) => {
        errorMixin.methods.displayMessage(
          'Error while converting custom scenarios.',
          e,
          'NumericalMissionErrorDisplay',
          'error'
        )
        return []
      })
    ).filter((reports) => reports != null)
    await commit(MutationTypes.UPDATE_CUSTOM_EVASIVE_REPORTS, customReports)
    await commit(MutationTypes.UPDATE_CUSTOM_EVASIVE_LOADING, false)
  }
}

export default {
  state,
  mutations,
  getters,
  actions
}
